import React, { useEffect, useState } from 'react';
import { Button, ProgressBar } from 'react-bootstrap';
import { HiRefresh } from 'react-icons/hi';
import { I18n } from 'react-redux-i18n';
import { formatDateBilling } from './CreditGrants/CreditGrants';
import { TAB_LIST } from '../../../BillingUsage';
import { BILLING_PLAN_TYPE } from '../../../../../features/signup/constantStep';
import { callApiWithoutDispatch } from '../../../../../utils/apiUtils';
import {
  onBoardingGetFleetUsageCountApi,
  scheduleCancelFleetBillingSubscriptionApi,
} from '../../../../../constants/ApiConfigs';
import {
  formatCurrencyHasSymbol,
  formatLongTypeToNumberCurrency,
} from '../../../../../utils/commonFunctions';
import moment from 'moment';
import { FLEET_BILLING_MODE } from '../../../utils/constants';

const calculateUsagePercentage = (UsageCount, addOnsSettings) => {
  if (!addOnsSettings?.allowance) {
    return 100;
  }
  if (!UsageCount?.usageCount) {
    return 0;
  }
  return ((UsageCount?.usageCount / addOnsSettings?.allowance) * 100).toFixed(
    1
  );
};

const mappingUsageConfig = (billingUsage) => {
  const currentDate = moment();
  return (
    billingUsage?.usageConfigs?.find((config) => {
      if (!config?.startDate || !config?.endDate) {
        return false;
      }
      const startDate = moment(config?.startDate).startOf('day');
      const endDate = moment(config?.endDate).endOf('day');
      return currentDate.isAfter(startDate) && currentDate.isBefore(endDate);
    }) || billingUsage?.usageConfigs?.[0]
  );
};

const getUsageConfig = (billingUsage) => {
  return mappingUsageConfig(billingUsage)?.[billingUsage?.billingType];
};

const getUsageTypeMappingOverage = (billingUsage, usageCount) => {
  let value = 0;

  const usageConfig = getUsageConfig(billingUsage);

  const rageExtraCount = usageConfig?.bookingTiers?.find((tier) => {
    let usageCountValue = usageCount.usageCount;

    if (mappingUsageConfig(billingUsage)?.usageType === 'block') {
      usageCountValue = (usageCountValue - usageConfig.allowance) / mappingUsageConfig(billingUsage)?.numOfBlock;
    }
    
    return (
      tier.from <= usageCountValue &&
      (tier.to || 99999999999) >= usageCountValue
    );
  });

  value = rageExtraCount?.feeAmount || 0;

  if (usageCount.usageCount <= usageConfig?.allowance) {
    value = 0;
  }

  switch (mappingUsageConfig(billingUsage)?.usageType) {
    case 'fixed':
      return (
        formatCurrencyHasSymbol(value, billingUsage?.currencyISO) + ' /req'
      );
    case 'percent':
      return value + '% /req';
    case 'block':
      return (
        formatCurrencyHasSymbol(value, billingUsage?.currencyISO) + ' /per block'
      );
  }
};

const FleetPlan = ({ billingUsage, onTabSelect, auth, context, ...props }) => {
  const [usageCount, setUsageCount] = useState(0);
  console.log(billingUsage, usageCount);
  let itemViewUsage = {};

  const onCancelSubscription = async () => {
    props.loadingActions.showLoadingSpiner();
    try {
      const response = await callApiWithoutDispatch(
        scheduleCancelFleetBillingSubscriptionApi,
        {
          method: 'POST',
          body: JSON.stringify({
            fleetId: auth?.selectedFleet?.fleetId,
            cancel: false,
          }),
        }
      );
      if (response?.res?.data) {
        context.notification('success', 'Subscription cancelled successfully');
        props.handleLoadBillingUsage();
      } else {
        context.notification('error', 'Failed to cancel subscription');
      }
    } catch (error) {
      context.notification('error', 'Failed to cancel subscription');
    } finally {
      props.loadingActions.hideLoadingSpiner();
    }
  };

  const handleViewUsage = async (item) => {
    try {
      props.loadingActions?.showLoadingSpiner();
      const response = await callApiWithoutDispatch(
        onBoardingGetFleetUsageCountApi,
        {
          method: 'POST',
          body: JSON.stringify({
            criteria: {
              fleetId: auth.selectedFleet.fleetId,
              startDate: moment(
                billingUsage?.subscription?.currentPeriodStart * 1000
              ).toISOString(),
              serviceType: 'booking',
              timezone: auth.selectedFleet.timezone,
            },
            from: 0,
            size: 5,
            fleetId: auth.selectedFleet.fleetId,
          }),
        }
      );
      if (response?.res) {
        setUsageCount(response?.res);
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      props.loadingActions.hideLoadingSpiner();
    }
  };

  const usagePercentage = calculateUsagePercentage(
    usageCount,
    getUsageConfig(billingUsage)
  );

  useEffect(() => {
    if (billingUsage?.planId) {
      handleViewUsage();
    }
  }, [billingUsage?.planId]);

  return (
    <div className="fleet-plan">
      <div className="plan-header">
        <h4>{I18n.t('billingUsage.fleetPlan')}</h4>
        {
          billingUsage?.billingMode !== FLEET_BILLING_MODE.invoice && (
            <Button
              variant="link"
              size="sm"
              className="billing-btn"
              onClick={() => onTabSelect(TAB_LIST.subscription)}
            >
              <HiRefresh size={16} className="mr-1" />
              {I18n.t('billingUsage.change')}
            </Button>
          )
        }
      </div>
      <div className="plan-details">
        <div className="plan-item">
          <p>
            <span>{I18n.t('billingUsage.currentPlan')}</span>
            <span>{billingUsage?.planName}</span>
          </p>
          <p>
            <span>{I18n.t('billingUsage.billing')}</span>
            <span>{`${formatCurrencyHasSymbol(billingUsage?.fee, billingUsage?.currencyISO)}/${
              billingUsage?.billingType === BILLING_PLAN_TYPE.annual
                ? I18n.t('billingUsage.annual')
                : I18n.t('billingUsage.monthly')
            }`}</span>
          </p>
        </div>
        <div className="usage-content">
          <p className="usage-description">
            {I18n.t('billingUsage.bookingUsage')}
          </p>
          <div className="usage-usage-container">
            <div className="usage-usage-container-item d-flex justify-content-between align-items-center">
              <span>{I18n.t('billingUsage.usageThisCycle')}</span>
              <span>{`${usagePercentage}%`}</span>
            </div>
            <ProgressBar now={usagePercentage} color="#04BE76" />
            <div className="usage-usage-container-item-value-container">
              <span>
                {usageCount.usageCount >
                getUsageConfig(billingUsage)?.allowance ? (
                  <>
                    {`${getUsageConfig(billingUsage)?.allowance} 
                  ${I18n.t('billingUsage.included')}`}
                    <span className="usage-overage-text">{` + ${
                      usageCount.usageCount -
                      getUsageConfig(billingUsage)?.allowance
                    } ${I18n.t('billingUsage.extra')}`}</span>
                  </>
                ) : (
                  `${usageCount.usageCount} ${I18n.t('billingUsage.of')} ${
                    getUsageConfig(billingUsage)?.allowance
                  } ${I18n.t('billingUsage.requests')}`
                )}
              </span>
              <span className="usage-usage-container-item-value-overage">
                +{' '}
                {formatCurrencyHasSymbol(
                  formatLongTypeToNumberCurrency(
                    usageCount.overage,
                    billingUsage?.currencyISO
                  ),
                  billingUsage?.currencyISO
                )}
              </span>
            </div>
          </div>
          <div className="usage-base-item">
            <span className="usage-base-item-title">
              {I18n.t('billingUsage.overages')}:
            </span>
            <span className="usage-base-item-value">
              {`${getUsageTypeMappingOverage(billingUsage, usageCount)}`}
            </span>
          </div>
        </div>

        {billingUsage?.cancelAtPeriodEnd === true ? (
          <div style={{ marginTop: '-15px' }}>
            <span
              style={{ fontSize: '12px' }}
              className="resume-subscription-text"
            >
              {I18n.t('billingUsage.endOfSubscription')}:{' '}
              {formatDateBilling(billingUsage?.currentPeriodEnd)}
            </span>
            <div style={{ textAlign: 'center' }} className="mt-2">
              <Button
                className="btn-header btn-cancel-subscription btn hasResume"
                onClick={() => onCancelSubscription()}
              >
                {I18n.t('billingUsage.resumeSubscription')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="plan-item">
            <p>
              <span>{I18n.t('billingUsage.nextBillingDate')}</span>
              <span>{formatDateBilling(billingUsage?.currentPeriodEnd)}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FleetPlan;
